import React from 'react';

import { useClassnames, visuallyHidden } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiChevronDown, mdiChevronUp } from '@lumapps/lumx/icons';
import { Alignment, FlexBox, Icon, IconProps, Placement, Size, Text, Theme, Tooltip } from '@lumapps/lumx/react';
import { useDisabledStateContext } from '@lumapps/lumx/react/utils';

import { ItemVariant, ViewMode } from '../../constants';
import { useNavigationContext } from '../../context';
import { useTooltipLabel } from '../../hooks/useTooltipLabel';

import './index.scss';

export interface NavigationItemProps extends Omit<React.ComponentPropsWithoutRef<'li'>, 'onClick'> {
    /** Content rendered inside the `<li>`, after the interactive element (e.g. a section's drawer). */
    afterItem?: React.ReactNode;
    /** Classname that will be used for the nav wrapping element */
    className?: string;
    /** the element used to create data attributes */
    element?: string;
    /** Icon (SVG path). */
    icon?: string;
    /** Additional props to pass to the icon */
    iconProps?: Omit<IconProps, 'icon'>;
    /**
     * Whether the item links to the current page (applies the selected style and sets aria-current="page").
     * Leave it undefined when `linkAs` sets `aria-current` on its own (ex: the router `NavLink`): the selected
     * style then follows the attribute in the DOM.
     */
    isCurrentPage?: boolean;
    /** Disabled state */
    isDisabled?: boolean;
    /** Whether the component represents a section */
    isSection?: boolean;
    /** Whether the section represented by the component is open */
    isSectionOpen?: boolean;
    /** Whether the item shows the selected style. It does not set aria-current. Use isCurrentPage for a page link. */
    isSelected?: boolean;
    /** Label content. */
    label: string | React.ReactNode;
    /**
     * Custom react component for the link (can be used to inject react router Link).
     * A component that sets `aria-current="page"` itself (ex: the router `NavLink`) gets the selected style
     * without `isCurrentPage`. The router `NavLink` needs a `NavLinkGroup` ancestor to elect one single link.
     */
    linkAs?: 'a' | React.ElementType;
    /** Props to pass to the link (minus those already set by the SideNavigationItem props). */
    linkProps?:
        | React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>
        | React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>
        | Record<string, any>;
    /** On click callback. */
    onClick?(evt: React.MouseEvent): void;
    /** The scope used to create data attributes */
    scope?: string;
    /** Theme that will be applied to the element, either Theme.dark or Theme.light */
    theme?: Theme;
    /** The variant used to determine whether to display text and/or icon. Default displays both */
    variant?: ItemVariant;
}

export const CLASSNAME = 'navigation-item';

const NavigationItem = React.forwardRef<HTMLLIElement, NavigationItemProps>((props, ref) => {
    const isGlobalDisabled = useDisabledStateContext()?.state === 'disabled';
    const { viewMode } = useNavigationContext();
    const {
        'aria-controls': ariaControls,
        'aria-expanded': ariaExpanded,
        afterItem,
        className,
        element,
        icon,
        iconProps,
        isCurrentPage,
        isSection,
        isSectionOpen,
        isSelected,
        isDisabled = isGlobalDisabled,
        label,
        linkAs,
        linkProps,
        onClick,
        scope,
        theme,
        variant,
        ...forwardedProps
    } = props;
    const { block: cssBlock, element: cssElement } = useClassnames(CLASSNAME);

    const { labelRef, tooltipLabel } = useTooltipLabel({ label, variant });

    /* istanbul ignore next */
    const { get } = useDataAttributes(scope ?? '');
    const dataAttributes = scope && element ? get({ element }) : {};

    const isHorizontalViewMode = viewMode === ViewMode.horizontal;

    const isLink = (!isDisabled && !isSection && linkProps && 'href' in linkProps && linkProps?.href) || linkAs;

    const Element: 'a' | 'button' | React.ElementType = isLink ? linkAs || 'a' : 'button';

    const elementProps: any = { ...linkProps };
    // Button (disabled)
    if (!isLink && isDisabled) {
        elementProps.disabled = true;
    }
    if (!isDisabled) {
        elementProps.onClick = onClick;
        elementProps.tabIndex = 0;
    }
    // The key is set only when the item declares itself the current page, and it is set after the linkProps
    // spread so that it wins. Any other case leaves the key out, so that a `linkAs` component that sets
    // `aria-current` on its own keeps its value instead of receiving an `undefined` that erases it.
    if (isCurrentPage) {
        elementProps['aria-current'] = 'page';
    }

    return (
        <li
            className={cssBlock({ horizontal: isHorizontalViewMode }, [className])}
            ref={ref}
            {...dataAttributes}
            {...forwardedProps}
        >
            <Tooltip label={tooltipLabel} placement={Placement.TOP}>
                <Element
                    aria-controls={ariaControls}
                    aria-expanded={ariaExpanded}
                    className={cssElement('link', {
                        'is-selected': Boolean(isCurrentPage || isSelected),
                        'is-disabled': isDisabled,
                        dark: theme === Theme.dark,
                    })}
                    {...elementProps}
                >
                    <FlexBox className={cssElement('flexbox')} hAlign={Alignment.center} fillSpace>
                        {!!icon && variant !== ItemVariant.labelOnly && (
                            <Icon
                                className={cssElement('icon', {
                                    'with-spacing': Boolean(variant !== ItemVariant.iconOnly || isSection),
                                })}
                                icon={icon}
                                size={Size.xs}
                                theme={theme}
                                {...iconProps}
                            />
                        )}
                        <Text
                            as="span"
                            className={cssElement('label', [variant === ItemVariant.iconOnly && visuallyHidden()])}
                            ref={labelRef}
                        >
                            {label}
                        </Text>

                        {isSection && (
                            <Icon
                                className={cssElement('chevron')}
                                icon={isSectionOpen ? mdiChevronUp : mdiChevronDown}
                            />
                        )}
                    </FlexBox>
                </Element>
            </Tooltip>
            {afterItem}
        </li>
    );
});

NavigationItem.displayName = 'NavigationItem';

export { NavigationItem };
